import { useTooltipConstants } from '../constants'
import type { LayoutType, TTooltipAlign, TTooltipSide } from '../types'
import { PositionAlignment, PositionDirection } from '../types/enums'

export const useLayout = () => {
  const tooltipConstants = useTooltipConstants()

  const HALF_DIVISOR = 2

  return {
    hasLayoutChanged(
      currentLayout: LayoutType,
      newLayout: { width: number; height: number }
    ): boolean {
      if (!currentLayout) return true

      return (
        Math.abs(currentLayout.width - newLayout.width) >
          tooltipConstants.LAYOUT_TOLERANCE ||
        Math.abs(currentLayout.height - newLayout.height) >
          tooltipConstants.LAYOUT_TOLERANCE
      )
    },

    calculateHorizontalMargin(
      triggerLayout: LayoutType,
      contentLayout: LayoutType,
      align: TTooltipAlign,
      screenWidth: number
    ): number {
      if (!triggerLayout || !contentLayout) return 0

      const triggerCenterX =
        triggerLayout.x + triggerLayout.width / HALF_DIVISOR
      const contentWidth = contentLayout.width

      const alignMap = {
        [PositionAlignment.MIDDLE]: () => {
          const idealLeft = triggerCenterX - contentWidth / HALF_DIVISOR
          const minLeft = tooltipConstants.SCREEN_PADDING
          const maxLeft =
            screenWidth - contentWidth - tooltipConstants.SCREEN_PADDING
          return Math.max(minLeft, Math.min(idealLeft, maxLeft))
        },
        [PositionAlignment.START]: () => {
          const idealLeft =
            triggerLayout.x +
            tooltipConstants.LATERAL_WIDTH_OFFSET +
            tooltipConstants.CONTENT_GAP
          const maxLeft =
            screenWidth - contentWidth - tooltipConstants.SCREEN_PADDING
          return Math.min(idealLeft, maxLeft)
        },
        [PositionAlignment.END]: () => {
          const idealLeft =
            triggerLayout.x +
            triggerLayout.width -
            contentWidth -
            (tooltipConstants.LATERAL_WIDTH_OFFSET +
              tooltipConstants.CONTENT_GAP)
          const minLeft = tooltipConstants.SCREEN_PADDING
          return Math.max(minLeft, idealLeft)
        },
      }
      return alignMap[align]?.() ?? 0
    },

    calculateVerticalMargin(
      triggerLayout: LayoutType,
      contentLayout: LayoutType,
      align: TTooltipAlign,
      screenHeight: number
    ): number {
      if (!triggerLayout || !contentLayout) return 0

      const triggerCenterY =
        triggerLayout.y + triggerLayout.height / HALF_DIVISOR
      const contentHeight = contentLayout.height

      const alignMap = {
        [PositionAlignment.MIDDLE]: () => {
          const idealTop = triggerCenterY - contentHeight / HALF_DIVISOR
          const minTop = tooltipConstants.SCREEN_PADDING
          const maxTop =
            screenHeight - contentHeight - tooltipConstants.SCREEN_PADDING
          return Math.max(minTop, Math.min(idealTop, maxTop))
        },
        [PositionAlignment.START]: () => {
          const idealTop = triggerLayout.y
          const maxTop =
            screenHeight - contentHeight - tooltipConstants.SCREEN_PADDING
          return Math.min(idealTop, maxTop)
        },
        [PositionAlignment.END]: () => {
          const idealTop =
            triggerLayout.y + triggerLayout.height - contentHeight
          const minTop = tooltipConstants.SCREEN_PADDING
          return Math.max(minTop, idealTop)
        },
      }
      return alignMap[align]?.() ?? 0
    },

    calculateLateralMargin(
      triggerLayout: LayoutType,
      contentLayout: LayoutType
    ): number {
      if (!triggerLayout || !contentLayout) return 0

      return Math.max(
        0,
        triggerLayout.x -
          tooltipConstants.SCREEN_MARGIN -
          tooltipConstants.CONTENT_GAP -
          contentLayout.width
      )
    },

    calculateRightLateralMargin(
      triggerLayout: LayoutType,
      contentLayout: LayoutType,
      screenWidth: number
    ): number {
      if (!triggerLayout || !contentLayout) return 0

      const triggerRightEdge = triggerLayout.x + triggerLayout.width
      const availableSpace =
        screenWidth -
        triggerRightEdge -
        tooltipConstants.SCREEN_MARGIN -
        tooltipConstants.CONTENT_GAP

      return Math.max(0, availableSpace - contentLayout.width)
    },

    calculateContentMargins(
      side: TTooltipSide,
      align: TTooltipAlign,
      triggerLayout: LayoutType,
      contentLayout: LayoutType,
      screenWidth: number,
      wrapperHeight: number
    ) {
      if (!triggerLayout || !contentLayout) {
        return { marginLeft: 0, marginTop: 0, marginRight: 0 }
      }

      const lateralMargin = this.calculateLateralMargin(
        triggerLayout,
        contentLayout
      )
      const rightLateralMargin = this.calculateRightLateralMargin(
        triggerLayout,
        contentLayout,
        screenWidth
      )
      const verticalMargin =
        wrapperHeight / HALF_DIVISOR - contentLayout.height / HALF_DIVISOR

      const marginMap = {
        [PositionDirection.TOP]: {
          marginLeft: this.calculateHorizontalMargin(
            triggerLayout,
            contentLayout,
            align,
            screenWidth
          ),
          marginTop: 0,
          marginRight: 0,
        },
        [PositionDirection.BOTTOM]: {
          marginLeft: this.calculateHorizontalMargin(
            triggerLayout,
            contentLayout,
            align,
            screenWidth
          ),
          marginTop: 0,
          marginRight: 0,
        },
        [PositionDirection.LEFT]: {
          marginLeft: lateralMargin,
          marginTop: verticalMargin,
          marginRight: 0,
        },
        [PositionDirection.RIGHT]: {
          marginLeft: 0,
          marginTop: verticalMargin,
          marginRight: rightLateralMargin,
        },
      }

      return marginMap[side] || { marginLeft: 0, marginTop: 0, marginRight: 0 }
    },

    getSideWrapperStyles(
      side: TTooltipSide,
      triggerLayout: LayoutType,
      contentLayout: LayoutType
    ) {
      const sideMap = {
        [PositionDirection.BOTTOM]: () => ({
          bottom: -(
            (contentLayout?.height || 0) + tooltipConstants.SCREEN_MARGIN
          ),
        }),
        [PositionDirection.TOP]: () => ({
          top: -((contentLayout?.height || 0) + tooltipConstants.SCREEN_MARGIN),
        }),
        [PositionDirection.LEFT]: () => ({
          left: -((triggerLayout?.x || 0) - tooltipConstants.CONTENT_GAP),
          bottom: -(contentLayout?.height || 0) / HALF_DIVISOR,
        }),
        [PositionDirection.RIGHT]: () => ({
          left: -((triggerLayout?.x || 0) + tooltipConstants.CONTENT_GAP),
          bottom: -(contentLayout?.height || 0) / HALF_DIVISOR,
        }),
      }
      return sideMap[side]?.() ?? {}
    },

    getMaxWidth(
      side: TTooltipSide,
      triggerLayout: LayoutType,
      screenWidth: number,
      themeMaxWidth: number
    ): number {
      const defaultMaxWidth = themeMaxWidth
      const sideMap = {
        [PositionDirection.BOTTOM]: () => defaultMaxWidth,
        [PositionDirection.TOP]: () => defaultMaxWidth,
        [PositionDirection.LEFT]: () =>
          triggerLayout
            ? triggerLayout.x -
              tooltipConstants.SCREEN_MARGIN -
              tooltipConstants.CONTENT_GAP
            : defaultMaxWidth,
        [PositionDirection.RIGHT]: () =>
          triggerLayout
            ? screenWidth -
              (triggerLayout.x + triggerLayout.width) -
              tooltipConstants.SCREEN_MARGIN -
              tooltipConstants.CONTENT_GAP
            : defaultMaxWidth,
      }
      return sideMap[side]?.() ?? defaultMaxWidth
    },
  }
}
